import {
  loadDynamicAssessmentResponsesCached,
  loadSelectedCapabilities,
  loadSubCapabilityAssessmentLegacy,
} from '../services/clientProjectData';
import {
  cachedClientFetch,
  clientCacheKey,
  mapWithConcurrency,
} from './clientProjectDataCache';
import type { DynamicAssessmentResponse } from '../types/dynamicAssessment';
import { DynamicAssessmentConstants } from '../types/dynamicAssessment';
import {
  buildSubCapabilitySubject,
  getResponseValue,
  responsesToMap,
} from './dynamicAssessmentUtils';
import { getSubCapabilityFormConfig } from '../hooks/useAssessmentFormConfig';

export interface DashboardAssessmentRow {
  capabilityId: number;
  subCapabilityId: number;
  currentStatusRating: number | null;
  futureStatusRating: number | null;
  currentStatusDescription: string;
  futureStatusDescription: string;
  businessValue?: string;
  businessValueImpact?: number | null;
}

export interface DashboardCapabilityGraphPoint {
  capabilityId: number;
  capabilityName: string;
  currentAverage: number;
  futureAverage: number;
}

export interface DashboardAssessmentData {
  rows: DashboardAssessmentRow[];
  graphPoints: DashboardCapabilityGraphPoint[];
  selectedCapabilities: Array<{
    capabilityId: number;
    capability: string;
    selectedSubCapabilities: Array<{ subCapabilityId: number; subCapability?: string }>;
  }>;
  maxRating: number;
  hasRatingData: boolean;
  hasDescriptionData: boolean;
}

const WORD_CLOUD_STOP_WORDS = new Set([
  'a', 'an', 'and', 'are', 'as', 'at', 'be', 'by', 'for', 'from', 'has', 'he', 'in', 'is',
  'it', 'its', 'of', 'on', 'that', 'the', 'to', 'was', 'were', 'will', 'with', 'this', 'have',
  'not', 'or', 'your', 'you', 'i', 'we', 'our', 'they', 'their', 'them', 'then', 'there', 'here',
  'am', 'shall',
]);

function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function mapDynamicResponsesToRow(
  capabilityId: number,
  subCapabilityId: number,
  responses: DynamicAssessmentResponse | null | undefined
): Partial<DashboardAssessmentRow> {
  const map = responsesToMap(responses?.responses ?? []);
  const fieldCodes = DynamicAssessmentConstants.fieldCodes;

  return {
    capabilityId,
    subCapabilityId,
    currentStatusRating: toNumber(getResponseValue(map[fieldCodes.currentRating])),
    futureStatusRating: toNumber(getResponseValue(map[fieldCodes.futureRating])),
    currentStatusDescription: String(getResponseValue(map[fieldCodes.currentDescription]) ?? '').trim(),
    futureStatusDescription: String(getResponseValue(map[fieldCodes.futureDescription]) ?? '').trim(),
    businessValue: String(getResponseValue(map[fieldCodes.businessValue]) ?? '').trim(),
    businessValueImpact: toNumber(getResponseValue(map[fieldCodes.businessValueImpact])),
  };
}

function mergeRow(
  legacy: Partial<DashboardAssessmentRow> | undefined,
  dynamic: Partial<DashboardAssessmentRow>
): DashboardAssessmentRow {
  return {
    capabilityId: legacy?.capabilityId ?? dynamic.capabilityId!,
    subCapabilityId: legacy?.subCapabilityId ?? dynamic.subCapabilityId!,
    currentStatusRating: legacy?.currentStatusRating ?? dynamic.currentStatusRating ?? null,
    futureStatusRating: legacy?.futureStatusRating ?? dynamic.futureStatusRating ?? null,
    currentStatusDescription:
      legacy?.currentStatusDescription || dynamic.currentStatusDescription || '',
    futureStatusDescription:
      legacy?.futureStatusDescription || dynamic.futureStatusDescription || '',
    businessValue: legacy?.businessValue || dynamic.businessValue || '',
    businessValueImpact: legacy?.businessValueImpact ?? dynamic.businessValueImpact ?? null,
  };
}

function hasRatings(row: DashboardAssessmentRow): boolean {
  return row.currentStatusRating != null && row.futureStatusRating != null;
}

function hasDescriptions(row: DashboardAssessmentRow): boolean {
  return Boolean(row.currentStatusDescription || row.futureStatusDescription);
}

export function computeMaxRating(
  rows: DashboardAssessmentRow[],
  selectedCapabilities: DashboardAssessmentData['selectedCapabilities']
): number {
  let max = 5;

  selectedCapabilities.forEach((capability) => {
    capability.selectedSubCapabilities?.forEach((subCapability) => {
      const config = getSubCapabilityFormConfig(subCapability);
      config.maturityOptions.forEach((option) => {
        max = Math.max(max, option.optionValue);
      });
    });
  });

  rows.forEach((row) => {
    if (row.currentStatusRating != null) {
      max = Math.max(max, row.currentStatusRating);
    }
    if (row.futureStatusRating != null) {
      max = Math.max(max, row.futureStatusRating);
    }
  });

  return max || 5;
}

export function buildCapabilityGraphPoints(
  rows: DashboardAssessmentRow[],
  selectedCapabilities: DashboardAssessmentData['selectedCapabilities']
): DashboardCapabilityGraphPoint[] {
  const capabilityNames = new Map<number, string>();
  selectedCapabilities.forEach((capability) => {
    capabilityNames.set(capability.capabilityId, capability.capability);
  });

  const grouped = new Map<number, { current: number[]; future: number[] }>();

  rows.forEach((row) => {
    if (!hasRatings(row)) {
      return;
    }

    const bucket = grouped.get(row.capabilityId) ?? { current: [], future: [] };
    bucket.current.push(row.currentStatusRating!);
    bucket.future.push(row.futureStatusRating!);
    grouped.set(row.capabilityId, bucket);
  });

  return Array.from(grouped.entries()).map(([capabilityId, values]) => ({
    capabilityId,
    capabilityName: capabilityNames.get(capabilityId) || `Capability ${capabilityId}`,
    currentAverage: Math.round(
      values.current.reduce((sum, value) => sum + value, 0) / values.current.length
    ),
    futureAverage: Math.round(
      values.future.reduce((sum, value) => sum + value, 0) / values.future.length
    ),
  }));
}

export function buildSliderChartData(
  selectedCapabilities: DashboardAssessmentData['selectedCapabilities'],
  rows: DashboardAssessmentRow[]
) {
  const capabilityMap: Record<
    number,
    { capabilityId: number; capabilityName: string; gaps: number[] }
  > = {};

  selectedCapabilities.forEach((capability) => {
    capabilityMap[capability.capabilityId] = {
      capabilityId: capability.capabilityId,
      capabilityName: capability.capability,
      gaps: [],
    };
  });

  rows.forEach((row) => {
    if (!capabilityMap[row.capabilityId] || !hasRatings(row)) {
      return;
    }

    capabilityMap[row.capabilityId].gaps.push(
      row.futureStatusRating! - row.currentStatusRating!
    );
  });

  const capabilities = Object.values(capabilityMap).filter((capability) => capability.gaps.length > 0);

  return {
    categories: capabilities.map((capability) => capability.capabilityName),
    series: [
      {
        name: 'Your Score',
        data: capabilities.map((capability) =>
          Math.round(
            capability.gaps.reduce((sum, gap) => sum + gap, 0) / capability.gaps.length
          )
        ),
      },
    ],
  };
}

export function buildWordCloudData(rows: DashboardAssessmentRow[]): Array<{ text: string; value: number }> {
  const wordCounts: Record<string, number> = {};

  rows.forEach((row) => {
    const text = `${row.currentStatusDescription || ''} ${row.futureStatusDescription || ''}`;
    text
      .split(/\s+/)
      .map((word) => word.trim().toLowerCase())
      .filter((word) => word && !WORD_CLOUD_STOP_WORDS.has(word) && word.length > 1)
      .forEach((word) => {
        wordCounts[word] = (wordCounts[word] || 0) + 2;
      });
  });

  return Object.entries(wordCounts).map(([text, value]) => ({ text, value }));
}

async function loadDashboardAssessmentDataUncached(
  userId: number,
  projectId: number
): Promise<DashboardAssessmentData> {
  const payload = { userId, projectId };

  const [selectedResponse, legacyResponse] = await Promise.all([
    loadSelectedCapabilities(payload),
    loadSubCapabilityAssessmentLegacy(payload).catch(() => ({ data: [] })),
  ]);

  const selectedCapabilities = selectedResponse?.data?.selectedCapabilities ?? [];
  const legacyRows: any[] = legacyResponse?.data ?? [];
  const legacyBySubId = new Map<number, any>(
    legacyRows.map((row) => [row.subCapabilityId, row])
  );

  type RowJob = {
    capabilityId: number;
    subCapabilityId: number;
    legacyPartial?: Partial<DashboardAssessmentRow>;
    needsDynamic: boolean;
  };

  const rowJobs: RowJob[] = [];

  selectedCapabilities.forEach((capability) => {
    (capability.selectedSubCapabilities ?? []).forEach((subCapability) => {
      const legacy = legacyBySubId.get(subCapability.subCapabilityId);
      const legacyPartial: Partial<DashboardAssessmentRow> | undefined = legacy
        ? {
            capabilityId: legacy.capabilityId ?? capability.capabilityId,
            subCapabilityId: legacy.subCapabilityId,
            currentStatusRating: toNumber(legacy.currentStatusRating),
            futureStatusRating: toNumber(legacy.futureStatusRating),
            currentStatusDescription: legacy.currentStatusDescription ?? '',
            futureStatusDescription: legacy.futureStatusDescription ?? '',
            businessValue: legacy.businessValue ?? '',
            businessValueImpact: toNumber(legacy.businessValueImpact),
          }
        : undefined;

      const needsDynamic =
        !legacyPartial?.currentStatusRating ||
        !legacyPartial?.futureStatusRating ||
        (!legacyPartial?.currentStatusDescription &&
          !legacyPartial?.futureStatusDescription);

      rowJobs.push({
        capabilityId: capability.capabilityId,
        subCapabilityId: subCapability.subCapabilityId,
        legacyPartial,
        needsDynamic,
      });
    });
  });

  const rows = await mapWithConcurrency(rowJobs, 6, async (job) => {
    let dynamicPartial: Partial<DashboardAssessmentRow> = {
      capabilityId: job.capabilityId,
      subCapabilityId: job.subCapabilityId,
    };

    if (job.needsDynamic) {
      try {
        const dynamic = await loadDynamicAssessmentResponsesCached(
          buildSubCapabilitySubject(userId, projectId, job.subCapabilityId)
        );
        dynamicPartial = mapDynamicResponsesToRow(
          job.capabilityId,
          job.subCapabilityId,
          dynamic
        );
      } catch {
        // No dynamic instance for this sub-capability yet.
      }
    }

    return mergeRow(job.legacyPartial, dynamicPartial);
  });
  const graphPoints = buildCapabilityGraphPoints(rows, selectedCapabilities);
  const maxRating = computeMaxRating(rows, selectedCapabilities);

  return {
    rows,
    graphPoints,
    selectedCapabilities,
    maxRating,
    hasRatingData: rows.some(hasRatings),
    hasDescriptionData: rows.some(hasDescriptions),
  };
}

export async function loadDashboardAssessmentData(
  userId: number,
  projectId: number
): Promise<DashboardAssessmentData> {
  const key = clientCacheKey(['dash_assess', userId, projectId]);
  return cachedClientFetch(
    key,
    () => loadDashboardAssessmentDataUncached(userId, projectId),
    {
      tags: ['dashboardAssessment'],
      userId,
      projectId,
    }
  );
}
